import { EventNote as CalendarIcon } from "@mui/icons-material";
import { FormHelperText, InputAdornment, SxProps, Theme } from "@mui/material";
import { DateTimePicker as MuiDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { DateTime } from "luxon";
import { toDateFormat } from "../../../helpers/date-helpers";
import type { ChangeHandler, DatePicker as TDatePicker } from "../types/Modify";
import { PickerChild } from "./PickerChild";

interface Props<D extends object> {
  config: Omit<TDatePicker<D>, "controltype">;
  handleChange: ChangeHandler;
}

const borderStyles: SxProps<Theme> = {
  borderRadius: 0,
};

export const DateTimePicker = <D extends object>({
  config,
  handleChange,
}: Props<D>) => {
  const {
    name,
    label,
    required,
    disabled,
    value,
    error,
    helperText,
    outputFormatter = toDateFormat,
  } = config;

  const dateTimeValue =
    typeof value === "string" && value ? DateTime.fromISO(value) : null;

  return (
    <>
      <MuiDateTimePicker
        renderInput={(props) => (
          <PickerChild
            name={name}
            {...props}
            required={required}
            error={error}
          />
        )}
        value={dateTimeValue}
        disabled={disabled}
        InputProps={{
          title: name,
          id: name,
          sx: [borderStyles],
          endAdornment: (
            <InputAdornment position="end">
              <CalendarIcon />
            </InputAdornment>
          ),
        }}
        onChange={(dateTime) => {
          if (!dateTime) return;
          const dateTimeString = outputFormatter(dateTime);
          handleChange({
            target: {
              name,
              value: dateTimeString,
            },
          });
        }}
        label={label}
      />
      <FormHelperText error={error} children={helperText} />
    </>
  );
};
